import Vue from 'vue';
import debounce from 'lodash.debounce';

const resolveChannelName = ({ repository, toolId, runId }) => `research.tool_run.${repository}.${toolId}.${runId}`;

function registerChannelListeners(channel, dispatch, commit, onSuccess, name = 'tool_run') {
  return channel
    .listen(`.${name}.success`, async ({ toolId, data, runId }) => {
      const { output, ...runData } = data;

      commit('setRunning', false);
      commit('setToolResults', { [toolId]: { values: data.output, ...runData, runId } });
      if (typeof onSuccess === 'function') {
        onSuccess(toolId, runId, data);
      }
    })
    .listen(`.${name}.failed`, ({ exception, toolId }) => {
      commit('setRunning', false);
      commit('setProcessing', false);
      commit('setToolErrors', { [toolId]: { message: exception } });
    })
    .listen(`.${name}.running`, () => {
      commit('setRunningStep', true);
      commit('setProcessing', false);
    })
    .listen(`.${name}.processing`, () => {
      commit('setProcessingStep', true);
      commit('setProcessing', true);
    });
}

const lazyJoinChannel = debounce(async (name, dispatch, commit, onSuccess) => {
  let channel = (await lazyLoadEcho(name)).private(name);

  return registerChannelListeners(channel, dispatch, commit, onSuccess);
}, 100);

async function lazyLoadEcho() {
  return (await import('../../../services/broadcasting/echo')).default;
}

export const RepositoryTools = {
  namespaced: true,

  state: {
    instanceDataIds: {},
    instances: {},
    instancesByTool: {},
    checkedInTools: [],
    stage: null,
    tools: [],
    uploads: [],
    toolResults: [],
    toolErrors: {},
    uploadsLoading: false,
    loading: false,
    saving: false,
    currentTool: {},
    processing: false,
    processingStep: false,
    running: false,
    runningStep: false,
    loadingData: false,
    toolData: [],
    toolUploads: {},
    canAdd: false,
    pagination: null,
    toolDetailsLoaded: false,
  },

  getters: {
    isToolInstanceLoaded: (state) => (uuid) => {
      return Object.keys(state.instances).includes(uuid);
    },

    getInstanceForToolAndSubject:
      ({ instancesByTool }) =>
      (tool, subject) =>
        instancesByTool?.[tool]?.[subject] ?? null,

    getUploadsForToolField:
      ({ toolUploads }) =>
      (toolId, instance, varName) => {
        console.log({ toolId, instance, varName, values: toolUploads[toolId] });
        const uploads = toolUploads[toolId]?.[instance] ?? {};

        return uploads[varName] ?? [];
      },

    getInstanceForModuleAndSubject:
      ({ instances }) =>
      (module, subject) => {
        return instances?.[module]?.[subject] ?? null;
      },

    getInstanceDataIds: (state) => (instanceId) => {
      return state.instanceDataIds[instanceId] ?? [];
    },

    getCurrentTool:
      ({ currentTool }) =>
      (uuid) => {
        if (!uuid) {
          const loadedTools = Object.values(currentTool);

          if (loadedTools.length === 1) {
            return loadedTools[0];
          }
        }

        return currentTool[uuid] ?? null;
      },

    getRepositoryCheckedInTools: (state) => (uuid) => {
      return state.checkedInTools[uuid] ?? [];
    },

    getRepositoryAWSTools: (state) => (uuid) => {
      return state.tools[uuid] ?? [];
    },

    getUploads: (state) => (varName, repositoryUuid, subjectUuid) => {
      if (subjectUuid) {
        return state.uploads[varName][repositoryUuid][subjectUuid];
      }
      return state.uploads[varName][repositoryUuid];
    },

    areCheckedInToolsLoaded: (state) => (uuid) => {
      return Object.keys(state.checkedInTools).includes(uuid);
    },

    areUploadsLoaded: (state) => (varName, repositoryUuid) => {
      return (
        Object.keys(state.uploads).includes(varName) && Object.keys(state.uploads[varName]).includes(repositoryUuid)
      );
    },

    areAWSToolsLoaded: (state) => (uuid) => {
      return Object.keys(state.tools).includes(uuid);
    },

    areToolResultsLoaded: (state) => (tool, instance) => {
      return (
        Object.keys(state.toolResults).includes(tool.uuid) &&
        Object.keys(state.toolResults[tool.uuid] ?? {}).includes(instance.toString())
      );
    },

    areToolErrors: (state) => (tool) => {
      return Object.keys(state.toolErrors).includes(tool.uuid);
    },

    getToolResults: (state) => (tool, instance) => {
      return state.toolResults[tool.uuid]?.[instance] ?? null;
    },

    getToolErrors: (state) => (tool) => {
      return state.toolErrors[tool.uuid];
    },

    loadToolData: (state) => (instanceId) => {
      return state.toolData?.[instanceId];
    },

    isUserAllowedToCreate: ({ canAdd }) => canAdd,

    areAWSToolsLoading: (state) => state.loading,

    areCheckedInToolsLoading: (state) => state.loading,

    areUploadsLoading: (state) => state.uploadsLoading,

    getProcessingStep: (state) => state.processingStep,

    getRunningStep: (state) => state.runningStep,

    getStage: (state) => state.stage,

    isProcessing: (state) => state.processing,

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    toolDetailsLoaded: (state) => state.toolDetailsLoaded,
  },

  mutations: {
    setToolInstanceForModuleAndSubject(state, { module, subject, instance }) {
      let moduleInstances = state.instances[module] ?? {};

      moduleInstances = { ...moduleInstances, [subject]: instance };

      state.instances = { ...state.instances, [module]: moduleInstances };
    },

    setToolInstanceForSubject(state, { tool, subject, instance }) {
      let instances = state.instancesByTool[tool] ?? {};

      instances = { ...instances, [subject]: instance };

      state.instancesByTool = { ...state.instancesByTool, [tool]: instances };
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setUploadsLoading(state, loading) {
      state.uploadsLoading = loading;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setProcessing(state, processing) {
      state.processing = processing;
    },

    setRunning(state, processing) {
      state.running = processing;
    },

    setRunningStep(state, runningStep) {
      state.runningStep = runningStep;
    },

    setProcessingStep(state, processingStep) {
      state.processingStep = processingStep;
    },

    clearToolErrors(state) {
      state.toolErrors = [];
    },

    setAWSTools(state, tools) {
      state.tools = tools;
    },

    setCheckedInTools(state, tools) {
      state.checkedInTools = tools;
    },

    setToolResults(state, results) {
      const toolIds = Object.keys(results);

      state.toolResults = toolIds.reduce((result, toolId) => {
        const instanceIds = Object.keys(results[toolId] ?? {});
        const existingInstances = state.toolResults[toolId] ?? {};

        const newInstances = instanceIds.reduce((instanceResult, instanceId) => {
          const instanceData = results[toolId]?.[instanceId] ?? {};

          return { ...existingInstances, ...instanceResult, [instanceId]: instanceData };
        }, {});

        return { ...state.toolResults, ...result, [toolId]: newInstances };
      }, {});
    },

    setToolErrors(state, message) {
      state.toolErrors = message;
    },

    setToolDetails(state, toolData) {
      state.currentTool = { ...state.currentTool, [toolData.data.uuid]: toolData };
    },

    setDataLoading(state, loading) {
      state.loadingData = loading;
    },

    setInstanceDataIds(state, { instance, dataIds }) {
      state.instanceDataIds = { ...state.instanceDataIds, [instance]: dataIds };
    },

    setToolData(state, { instance, data }) {
      state.toolData = { ...state.toolData, [instance]: data };
    },

    setUploads(state, { uploadsData, varName, repositoryUuid, subjectUuid }) {
      const data = uploadsData.map((item) => {
        return {
          ...item,
          _showDetails: false,
        };
      });

      const currentVarname = state.uploads[varName] ?? {};
      const currentValues = currentVarname[repositoryUuid] ?? {};

      let newUploads = { ...state.uploads, [varName]: { ...currentVarname, [subjectUuid]: data } };

      if (subjectUuid) {
        newUploads = state.uploads = {
          ...state.uploads,
          [varName]: { ...currentVarname, [repositoryUuid]: { ...currentValues, [subjectUuid]: data } },
        };
      }

      state.uploads = newUploads;
    },

    setStage(state, { stage }) {
      state.stage = stage.uuid;
    },

    setCanAdd(state, canAdd) {
      state.canAdd = canAdd;
    },

    setToolOutputUploads(state, uploadData) {
      const toolIds = Object.keys(uploadData);

      state.toolUploads = toolIds.reduce((result, toolId) => {
        const instanceIds = Object.keys(uploadData[toolId] ?? {});
        const existingInstances = state.toolResults[toolId] ?? {};

        const newInstances = instanceIds.reduce((instanceResult, instanceId) => {
          const instanceData = uploadData[toolId]?.[instanceId] ?? {};

          return { ...existingInstances, ...instanceResult, [instanceId]: instanceData };
        }, {});

        return { ...state.toolResults, ...result, [toolId]: newInstances };
      }, {});
    },

    toggleRowDetails(state, { item, varName, repositoryUuid, details, subjectUuid }) {
      let uploads = subjectUuid
        ? state.uploads[varName][repositoryUuid][subjectUuid]
        : state.uploads[varName][repositoryUuid];
      uploads.find(function (data) {
        if (data.id === item.id && details !== null) {
          data.details = details;
          data._showDetails = data.hasOwnProperty('_showDetails') ? !data._showDetails : true;
        }
      });
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    setToolDetailsLoaded(state, bool) {
      Vue.set(state, 'toolDetailsLoaded', bool);
    },
  },

  actions: {
    async loadUploads({ commit, getters }, { varName, repository, uploadTypes, subject, ...rest }) {
      if (!repository || !subject) {
        return;
      }

      let routeParams = { version: '1.0', repository };
      if (subject) {
        routeParams.subject = subject;
      }

      if (getters.getStage) {
        routeParams.stage = getters.getStage;
      }

      commit('setUploadsLoading', true);
      try {
        let params = { ...routeParams, ...rest };

        if (!params.stage) {
          params.stage = 'all';
        }

        const response = await this._vm.$api('repository.uploads', params, {
          uploadTypes,
        });

        const uploadsData = response.data;
        commit('setUploads', { uploadsData, varName, repositoryUuid: repository, subjectUuid: subject });
      } catch (err) {
        console.error(err);
      }

      commit('setUploadsLoading', false);
    },

    async loadToolDetails({ commit }, { repository, tool }) {
      if (!repository || !tool) {
        return;
      }

      commit('setLoading', true);
      commit('setToolDetailsLoaded', false);
      try {
        const response = await this._vm.$api('repositories.tools.show', {
          version: '1.0',
          repository,
          tool,
        });

        const toolData = response.data;
        commit('setToolDetails', toolData);
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
      commit('setToolDetailsLoaded', true);
    },

    async loadAWSTools({ commit }, { repository }) {
      if (!repository) {
        return;
      }

      let tools = null;
      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.aws-lambda-endpoints', {
          version: '1.0',
          repository,
        });
        tools = response.data;
      } catch (err) {
        console.error(err);
      }

      commit('setAWSTools', { [repository]: tools });
      commit('setLoading', false);
    },

    async checkInTool({ commit }, { repository, tool }) {
      commit('setSaving', true);

      await this._vm.$api(
        'repositories.tools.store',
        {
          version: '1.0',
          repository,
        },
        tool
      );

      commit('setSaving', false);
    },

    async loadRepositoryCheckedInTools({ commit }, { repository, filter, page, perPage, sortBy, sortDesc }) {
      if (!repository) {
        return;
      }

      let tools = null;
      let canAdd = false;
      let pagination = {};

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.tools.index', {
          version: '1.0',
          repository,
          filter,
          page,
          perPage,
          sortBy,
          sortDesc,
        });

        tools = response.data.data;
        canAdd = response.data.can?.add ?? false;
        pagination = { ...response.data.meta, ...response.data.links };
      } catch (err) {
        console.error(err);
      }

      commit('setPagination', { [repository]: pagination });
      commit('setCheckedInTools', { [repository]: tools });
      commit('setCanAdd', canAdd);
      commit('setLoading', false);
    },

    async processAndRunTool({ commit, dispatch }, { repository, project, subject, instance, tool, formTool }) {
      commit('setRunningStep', false);
      commit('setRunning', false);
      commit('setProcessing', true);
      commit('setProcessingStep', true);
      commit('clearToolErrors');

      let inputArray = [];

      try {
        commit('setProcessing', false);

        await dispatch('runTool', { repository, project, subject, tool, instance, formTool, files: [], inputArray });
      } catch (err) {
        console.error(err);
      }
    },

    async runTool({ commit, dispatch }, { repository, project, subject, tool, instance, formTool, files, inputArray }) {
      commit('setRunningStep', true);
      commit('setRunning', true);

      if (!formTool) {
        return;
      }

      let request = Object.assign({}, formTool);
      let images = [];

      const inputData = inputArray.reduce((data, inputField) => {
        images = formTool[inputField].map((instance) => {
          return files[instance] ?? instance;
        });

        return { ...data, [inputField]: images.flat(1) };
      }, {});

      request = {
        input: {
          ...request,
          ...inputData,
        },
        instanceId: instance,
      };

      let routeParams = { version: '1.0', repository, project, subject, tool: tool.uuid };

      const response = await this._vm.$api('repositories.tools.run', routeParams, request);

      if (response.data.status === 'failed') {
        commit('setToolErrors', {
          [tool.uuid]: { message: 'Whoops! An Error has just occurred: ' + response.data.error },
        });

        return;
      }

      const runId = response.data.data;

      const channelName = resolveChannelName({ repository, toolId: tool.uuid, runId });
      await lazyJoinChannel(channelName, dispatch, commit, async (_, runId, { output, uploads }) => {
        commit('setToolResults', {
          [tool.uuid]: {
            [instance]: {
              values: output,
              runId,
              fields: tool.outputDefinition ?? [],
              uploads,
            },
          },
        });

        commit('setToolOutputUploads', {
          [tool.uuid]: { [instance]: uploads },
        });

        dispatch('getInstanceData', { tool: tool.uuid, instance });
      });
    },

    async saveToolResults({ commit }, { repository, tool, instance, initialRequest, results, runId }) {
      try {
        let requestBody = { inputValues: initialRequest, outputValues: results, runId };
        let routeParams = { version: '1.0', repository, tool: tool.uuid, instance: instance };
        const response = await this._vm.$api('repositories.tool.saveResults', routeParams, requestBody);

        let dataIds = response.data.data.dataIds;

        commit('setInstanceDataIds', { instance, dataIds: dataIds });
      } catch (err) {
        console.error(err);
      }
    },

    async loadToolInstance({ commit }, { repository, module, subject, project, event, pipeline, version, readOnly, runVersion }) {
      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.tool.instance', {
          version: '1.0',
          repository,
          project,
          pipeline,
          event,
          module,
          subject,
          v: version,
          read_only: readOnly ? '1' : null,
          run_version: runVersion,
        });

        const instance = response.data;
        commit('setToolInstanceForModuleAndSubject', { module, subject, instance });
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async loadToolStandaloneInstance({ commit }, { repository, subject, project, tool }) {
      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.tool.standalone_instance', {
          version: '1.0',
          repository,
          project,
          subject,
          tool,
        });

        const instance = response.data;
        commit('setToolInstanceForSubject', { tool, subject, instance });
      } catch (err) {
        console.error(err);
      }

      commit('setLoading', false);
    },

    async getInstanceData({ commit }, { instance, tool }) {
      if (!instance || !tool) {
        return;
      }

      commit('setDataLoading', true);
      try {
        const response = await this._vm.$api('research.tool.instance.data', {
          version: '1.0',
          instance,
        });

        let dataIds = response.data.dataIds;
        let dataTool = response.data.tool;

        if (tool) {
          const { values, fields, uploads } = dataTool;

          const outputFields = (fields?.output ?? []).map((field) => ({ ...field, varName: field.var_name }));

          commit('setToolOutputUploads', {
            [tool.uuid]: { [instance]: uploads },
          });
          commit('setToolResults', { [tool]: { [instance]: { values, fields: outputFields, uploads } } });
        }

        commit('setToolData', { instance, data: dataTool });
        commit('setInstanceDataIds', { instance, dataIds: dataIds });
      } catch (err) {
        console.error(err);
      }
      commit('setDataLoading', false);
    },
  },
};
